import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, CreditCard, Fuel, Plane, Clock, BadgeCheck, FileText, Users } from "lucide-react";

const FAQS = [
  { icon: BadgeCheck, q: "What do I need to rent a car in Nassau?", a: "A valid driver's license (held 2+ years), and you must be 25 or older. Bahamian renters also provide their NIB number; international visitors provide a passport number and flight details. Everything can be uploaded from your phone before pickup — no paperwork at the counter." },
  { icon: CreditCard, q: "Is there a security deposit?", a: "Yes — a $150 refundable hold placed on your card at pickup and released after a clean return. Platinum Islander loyalty members (33+ rentals) enjoy a reduced $100 hold. The hold is an authorization, not a charge." },
  { icon: Plane, q: "Do you deliver to the airport or my hotel?", a: "We offer free delivery and collection anywhere on New Providence and Paradise Island — hotels, villas, marinas and homes. Airport pickup and drop-off at Lynden Pindling International (NAS) is available for a small fee, and if you share your flight number we track it so delays never leave you stranded." },
  { icon: ShieldCheck, q: "Is insurance included?", a: "Basic insurance is included in every rental. For extra peace of mind you can add Supplementary Liability Insurance (SLI) for $7.99/day, which covers third-party liability with a $500 deductible per incident. Add it during booking — it comes pre-selected as our recommended option." },
  { icon: Fuel, q: "What is the fuel policy?", a: "Simple same-to-same: the car goes out with a noted fuel level (usually full) and comes back the same. Return it lower and a refueling charge of $32 plus the fuel difference applies — the exact amounts are shown at checkout and on your contract." },
  { icon: Clock, q: "What if my plans change or I return late?", a: "Cancel free of charge up to 24 hours before pickup for a full refund. Running late on return day? Extend instantly from your phone — returns more than an hour late without an extension are billed at $25/hour, all shown in your rental agreement." },
  { icon: Users, q: "Can I add another driver?", a: "Yes — additional drivers are $25 each per rental and must be 25+ with a valid license. Add them during booking or at pickup; their license details are captured digitally just like yours." },
  { icon: FileText, q: "How do I pay, and do I sign anything?", a: "Pay online by card (Stripe or PayPal), Zelle, or bank wire. Your rental agreement is signed digitally from your phone before pickup — locals with 5+ verified rentals often skip the counter entirely with express check-in." },
];

export default function FAQ() {
  useSEO({
    title: "FAQ — Nassau Car Rental Questions Answered | Easy Drive",
    description: "Deposits, license rules, airport delivery, insurance, fuel policy and more — every common question about renting a car in Nassau, Bahamas, answered by Easy Drive Car Rental.",
    path: "/faq",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  });

  return (
    <div data-testid="faq-page" className="max-w-4xl mx-auto px-6 lg:px-10 pt-36 pb-24">
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Good to know</p>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight">
        Renting in Nassau, <span className="italic gold-text">answered</span>
      </h1>
      <p className="mt-6 text-muted-foreground leading-relaxed font-ui max-w-2xl">
        Deposits, licenses, airport delivery, insurance — the questions every visitor asks, answered straight by the Easy Drive team on West Bay Street.
      </p>

      <div className="mt-12 space-y-5">
        {FAQS.map(({ icon: Icon, q, a }, i) => (
          <div key={i} data-testid={`faq-item-${i + 1}`} className="bg-card border border-border rounded-2xl p-6 md:p-7 flex gap-5 transition-colors duration-200 hover:border-primary/40">
            <span className="w-11 h-11 rounded-full border border-primary/30 flex items-center justify-center shrink-0">
              <Icon size={18} className="gold-text" />
            </span>
            <div>
              <h2 className="font-display text-xl tracking-tight">{q}</h2>
              <p className="mt-2 text-sm text-muted-foreground font-ui leading-relaxed">{a}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-14 bg-card border border-border rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div>
          <h2 className="font-display text-2xl tracking-tight">Ready when you are</h2>
          <p className="mt-1.5 text-sm text-muted-foreground font-ui">Free delivery anywhere on the island — book in under two minutes.</p>
        </div>
        <Link to="/fleet">
          <Button data-testid="faq-book-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-7 h-11">
            Browse the fleet <ArrowRight size={15} className="ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
